import React, { useEffect, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { Box } from '@mui/material';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: boolean;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter content here...',
  error = false
}) => {
  // Single container and instance references
  const containerRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);
  const ignoreChanges = useRef(false);
  
  // Initialize Quill on mount
  useEffect(() => {
    // Only initialize if not already initialized and container exists
    if (containerRef.current && !quillRef.current) {
      // Define toolbar options
      const toolbarOptions = [
        ['bold', 'italic', 'underline'],
        [{ 'header': [1, 2, 3, 4, false] }],
        [{ 'align': [] }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        ['link', 'image'],
        [{ 'color': [] }]
      ];
      
      // Create Quill with default toolbar structure
      const quill = new Quill(containerRef.current, {
        modules: {
          toolbar: toolbarOptions
        },
        placeholder: placeholder,
        theme: 'snow'
      });
      
      // Store the instance
      quillRef.current = quill;
      
      // Set initial content
      if (value) {
        ignoreChanges.current = true;
        quill.clipboard.dangerouslyPasteHTML(value);
        
        // Position cursor at the end
        const length = quill.getLength();
        quill.setSelection(length, 0);
        ignoreChanges.current = false;
      }
      
      // Set up change handler
      quill.on('text-change', () => {
        if (!ignoreChanges.current) {
          onChange(quill.root.innerHTML);
        }
      });
    }
    
    // Clean up on unmount
    return () => {
      if (quillRef.current) {
        quillRef.current.off('text-change');
        quillRef.current = null;
      }
    };
  }, []);
  
  // Update content when value prop changes
  useEffect(() => {
    if (quillRef.current && value !== quillRef.current.root.innerHTML) {
      ignoreChanges.current = true;
      quillRef.current.clipboard.dangerouslyPasteHTML(value || '');
      
      // Position cursor at the end
      const length = quillRef.current.getLength();
      quillRef.current.setSelection(length, 0);
      
      ignoreChanges.current = false;
    }
  }, [value]);
  
  // Update placeholder when it changes
  useEffect(() => {
    if (quillRef.current) {
      quillRef.current.root.dataset.placeholder = placeholder;
    }
  }, [placeholder]);
  
  return (
    <Box 
      sx={{
        border: error ? '1px solid #f44336' : 'none',
        borderRadius: 1,
        '& .ql-container': {
          minHeight: '280px',
          fontSize: '1rem'
        },
        '& .ql-toolbar, & .ql-container': {
          border: error ? '1px solid #f44336' : undefined
        }
      }}
    >
      <div ref={containerRef}></div>
    </Box>
  );
};

export default RichTextEditor; 